import type { Booking, Deposit } from '@/domain/types';
import {
  Send, Eye, CheckCircle2, CalendarPlus, AlertTriangle, RotateCw, CalendarX, Ban,
  PieChart, Repeat, CheckCheck, Undo2, Store,
} from 'lucide-react';
import { depositPaidAud, isDepositRedeemed, type PayAtStudioState } from '@/utils/bookingSummary';
import { actorLabel } from '@/domain/actor';
import { aud } from '@/utils/format';
import { isLinkEffectivelyExpired, linkSenderPersonId, linkSenderRole, piecePotAud, slotBookingId, type LinkSenderRole } from '@atink/domain';

export type DepositDisplayStatus =
  | 'sent' | 'viewed' | 'paid' | 'placed' | 'lapsed' | 'forfeited' | 'expired' | 'link-resent'
  // W5b (tv-2na.8.23 / 11.17): states the display used to fold into Sent /
  // Expired / Placed. part-paid = some money in, less than the deposit;
  // rolled-over / refunded = the money moved on; used = consumed at completion.
  | 'part-paid' | 'rolled-over' | 'used' | 'refunded'
  // B4 (8tqu): a hold the client pays in person at the desk; no link was sent.
  | 'pay-at-studio';

/**
 * Money down but the session was a no-show. NOTE: the runbook also floated "or
 * the session date passed unattended", but this seed leaves finished sessions as
 * `confirmed` rather than `completed`, so a past-date heuristic mislabels ~1000
 * healthy placed deposits as lapsed. We therefore key `lapsed` off the explicit
 * `no-show` mark only (the reliable signal; G3 wires the no-show action to it).
 */
function isLapsed(b: Booking): boolean {
  return b.status === 'no-show';
}

/**
 * L2 (17 Jul): the money is actually kept once a `forfeit` movement lands (past
 * the 8h window). `lapsed` is the earlier state - paid + no-showed but not yet
 * forfeited. A later `reinstate` reverses the forfeit, so we treat the deposit
 * as forfeited only while its most recent forfeit is unreversed.
 */
function isForfeited(d: Deposit): boolean {
  const ledger = d.ledger ?? [];
  let lastForfeit = -1;
  let lastReinstate = -1;
  ledger.forEach((e, i) => {
    if (e.kind === 'forfeit') lastForfeit = i;
    if (e.kind === 'reinstate') lastReinstate = i;
  });
  return lastForfeit >= 0 && lastReinstate < lastForfeit;
}

/** A deposit kept by the house right now: a stored 'forfeited' or an unreversed forfeit movement. */
export function isDepositForfeited(d: Deposit): boolean {
  return d.status === 'forfeited' || isForfeited(d);
}

/** The amount a forfeit kept (the latest forfeit movement, else the face value). */
export function forfeitedAud(d: Deposit): number {
  const last = [...(d.ledger ?? [])].reverse().find((e) => e.kind === 'forfeit');
  return last?.amountAud ?? d.amountAud;
}

/**
 * Single source of truth for a deposit's display status (ledger + artist list).
 * Pass the linked booking to surface the G2 `lapsed` / `forfeited` states.
 */
export function deriveDepositStatus(d: Deposit, nowMs: number, booking?: Booking): DepositDisplayStatus {
  // The money moved on: these stored states are final, whatever the link clock says.
  if (d.status === 'rolled-over') return 'rolled-over';
  if (d.status === 'refunded') return 'refunded';
  // Ledger evidence (an unreversed forfeit) wins over the booking-status lapsed
  // read, so paid -> lapsed -> forfeited reads as one progression on the drawer.
  if (d.status === 'forfeited' || isForfeited(d)) return 'forfeited';
  if (isDepositRedeemed(d)) return 'used';
  const paidAud = depositPaidAud(d);
  // A pay-at-studio hold is never on the link clock and was never sent (8tqu).
  if (d.status === 'pending' && d.payAtStudio && paidAud <= 0) return 'pay-at-studio';
  // Expired from the pay-by instant, not the next sweep — the ONE rule Ops and the
  // customer page read too (@atink/domain isLinkEffectivelyExpired, B4 7kv4 / 7g3y).
  // The link expiry never applies once money is in (tv-2na.8.10 / 11.17).
  if (isLinkEffectivelyExpired({ ...d, amountPaid: paidAud }, new Date(nowMs).toISOString())) return 'expired';
  if (d.status === 'paid' && booking && isLapsed(booking)) return 'lapsed';
  if (paidAud > 0 && paidAud < d.amountAud) return 'part-paid';
  if (d.status === 'paid' && d.bookingId) return 'placed';
  if (d.status === 'paid') return 'paid';
  if (d.resentAt) return 'link-resent';
  if (d.viewedAt) return 'viewed';
  return 'sent';
}

/** Whether a deposit link was sent by an artist (self-book) or the office. */
export function depositOrigin(d: Deposit): 'artist' | 'office' {
  // A recorded staff sender (W5 sentByRole) is always the office.
  if (d.sentByRole && d.sentByRole !== 'self-book') return 'office';
  if (d.source === 'artist-link' || (d.depositReference && !d.takenByAgentId)) return 'artist';
  return 'office';
}

export const SENDER_ROLE_WORD: Record<LinkSenderRole, string> = {
  agent: 'Agent',
  messenger: 'Messenger',
  sweeper: 'Sweeper',
  'self-book': 'Self-book',
};

/**
 * A link's sender in words, the ONE label the ledger and Ops both show
 * (tv-7n5f, 7kv4): the person's name where known, else the role.
 */
export function linkSenderLabel(personId: string | undefined, role: LinkSenderRole): string {
  return personId ? actorLabel(personId) : SENDER_ROLE_WORD[role];
}

/**
 * The ledger's SENT BY (tv-2na.12.19, D10): "Artist" for an artist link, else the
 * sender by the same projection Ops reads (@atink/domain linkSenderPersonId /
 * linkSenderRole): the person (sentBy, else the agent who took it), else the role.
 * A Messenger link used to read OFFICE because only takenByAgentId was checked,
 * and an older agent link read by name here but "Agent" on Ops (tv-7n5f).
 */
export function depositSentByLabel(d: Deposit): string {
  if (depositOrigin(d) === 'artist') return 'Artist';
  return linkSenderLabel(linkSenderPersonId(d), linkSenderRole(d));
}

/** Money actually held on a deposit (the ledger net, else amountPaid). */
export function depositHeldAud(d: Deposit): number {
  return depositPaidAud(d);
}

/**
 * The honest amount (tv-2na.8.18): "held $200 of $600" while only part of the
 * deposit is in, else the face value. Every surface that quotes a deposit's
 * amount (ledger, Sweeper deposit rung, customer card) reads this.
 */
export function depositAmountLabel(d: Deposit): string {
  const held = depositPaidAud(d);
  if (held > 0 && held < d.amountAud) return `held ${aud(held)} of ${aud(d.amountAud)}`;
  return aud(d.amountAud);
}

export const DEPOSIT_STATUS_META: Record<
  DepositDisplayStatus,
  { label: string; tone: string; icon: typeof Send }
> = {
  sent: { label: 'Sent', tone: 'bg-cream-soft/15 text-cream-soft', icon: Send },
  viewed: { label: 'Viewed', tone: 'bg-gold/20 text-gold-bright', icon: Eye },
  paid: { label: 'Paid', tone: 'bg-mint/20 text-mint', icon: CheckCircle2 },
  placed: { label: 'Placed', tone: 'bg-mint/30 text-mint', icon: CalendarPlus },
  lapsed: { label: 'Lapsed', tone: 'bg-blood/25 text-blood', icon: CalendarX },
  // Distinct muted ochre (not blood-red) - the deposit is kept, but reversible.
  forfeited: { label: 'Forfeited', tone: 'bg-orange/20 text-orange', icon: Ban },
  expired: { label: 'Expired', tone: 'bg-blood/20 text-blood', icon: AlertTriangle },
  'link-resent': { label: 'Link resent', tone: 'bg-azure/15 text-azure', icon: RotateCw },
  'part-paid': { label: 'Part-paid', tone: 'bg-gold/20 text-gold-bright', icon: PieChart },
  'rolled-over': { label: 'Rolled over', tone: 'bg-cream-soft/15 text-cream-soft', icon: Repeat },
  used: { label: 'Used', tone: 'bg-mint/15 text-mint', icon: CheckCheck },
  refunded: { label: 'Refunded', tone: 'bg-cream-soft/15 text-cream-soft', icon: Undo2 },
  'pay-at-studio': { label: 'Pay at studio', tone: 'bg-azure/15 text-azure', icon: Store },
};

/**
 * The booking's pay-at-studio hold (B4, 8tqu): its still-pending, pay-on-arrival
 * deposit, found by the booking's depositId or by the deposit's slot key
 * (heldBookingId ?? bookingId). Undefined once paid, or for any other booking.
 */
export function payAtStudioDepositFor(
  booking: Pick<Booking, 'id' | 'depositId'>,
  deposits: Record<string, Deposit>,
): Deposit | undefined {
  const isHold = (d: Deposit | undefined): d is Deposit =>
    !!d && d.payAtStudio === true && d.status === 'pending' && depositPaidAud(d) <= 0;
  const linked = booking.depositId ? deposits[booking.depositId] : undefined;
  if (isHold(linked)) return linked;
  return Object.values(deposits).find((d) => isHold(d) && slotBookingId(d) === booking.id);
}

/**
 * Is this booking a pay-at-studio one, and has the desk taken the money yet
 * (tv-958x)? Same lookup as payAtStudioDepositFor (depositId, else the slot key)
 * but whatever the deposit's state, so a booking paid on arrival still reads as
 * paid at the studio rather than falling back to the link placeholder.
 */
export function payAtStudioStateFor(
  booking: Pick<Booking, 'id' | 'depositId'>,
  deposits: Record<string, Deposit>,
): PayAtStudioState | undefined {
  const linked = booking.depositId ? deposits[booking.depositId] : undefined;
  const d =
    linked?.payAtStudio === true
      ? linked
      : Object.values(deposits).find((x) => x.payAtStudio === true && slotBookingId(x) === booking.id);
  if (!d) return undefined;
  return payAtStudioDepositFor(booking, { [d.id]: d }) ? 'due' : 'paid';
}

/**
 * The deposit pot a piece holds (B4, 75mu; Anas 1 Oct Q1): the net of its deposit
 * chain — every deposit stamped with the piece, or on one of the piece's
 * bookings (older rows predate the stamp) — rollovers moving money inside it.
 */
export function piecePotFor(
  pieceId: string,
  deposits: Record<string, Deposit>,
  bookings: Record<string, Booking>,
): number {
  const chain = Object.values(deposits).filter((d) => {
    if (d.pieceId === pieceId) return true;
    const slot = slotBookingId(d);
    return !!slot && bookings[slot]?.pieceId === pieceId;
  });
  return piecePotAud(chain);
}
